import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, MaskedLines } from "../components/Reveal";
import client, { apiError } from "../lib/api";

const INTERESTS = [
  { id: "sketches", label: "Sketches" },
  { id: "standup", label: "Stand-up" },
  { id: "tapings", label: "Live tapings" },
  { id: "cities", label: "Future cities" },
];

export default function Join() {
  const [form, setForm] = useState({ email: "", first_name: "", city: "", interests: [] });
  const [status, setStatus] = useState({ type: "idle", text: "" });
  const [loading, setLoading] = useState(false);

  const toggle = (id) =>
    setForm((f) => ({ ...f, interests: f.interests.includes(id) ? f.interests.filter((x) => x !== id) : [...f.interests, id] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await client.post("/signup", form);
      setStatus({ type: "ok", text: data.message });
      setForm({ email: "", first_name: "", city: "", interests: [] });
    } catch (err) {
      setStatus({ type: "err", text: apiError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-16" data-testid="join-page">
      <Seo title="Join the List" description="Join the Laugh at L.A. list for sketches, stand-up clips, live taping invites, and news about future cities." />
      <section className="spotlight-bg min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 py-20 sm:py-28">
          <Reveal className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">The List</p>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red">
              <MaskedLines lines={["FRONT ROW,", "EVERY TIME."]} />
            </h1>
            <p className="mt-5 text-gray-300">Sketches, stand-up clips, live taping invites, and news on future Laugh @ cities. No spam — just the good seats.</p>
          </Reveal>

          <Reveal delay={0.15}>
            {status.type === "ok" ? (
              <div className="mt-10 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-10 text-center" data-testid="join-success">
                <CheckCircle2 size={40} className="mx-auto text-emerald-400" />
                <p className="mt-4 font-display text-3xl text-white">{status.text}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 rounded-2xl border border-white/10 bg-[#181824]/70 backdrop-blur-xl p-8 space-y-5" data-testid="join-form">
                <div>
                  <label htmlFor="join-email" className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B]">Email *</label>
                  <input id="join-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@email.com" data-testid="join-email"
                    className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="join-name" className="text-xs font-semibold uppercase tracking-widest text-gray-400">First name (optional)</label>
                    <input id="join-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      placeholder="What should we call you?" data-testid="join-first-name"
                      className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                  </div>
                  <div>
                    <label htmlFor="join-city" className="text-xs font-semibold uppercase tracking-widest text-gray-400">City (optional)</label>
                    <input id="join-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                      placeholder="Where do you laugh?" data-testid="join-city"
                      className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-5 py-3.5 text-white placeholder-gray-500 outline-none focus:border-[#F59E0B]" />
                  </div>
                </div>
                <fieldset>
                  <legend className="text-xs font-semibold uppercase tracking-widest text-gray-400">I'm here for (optional)</legend>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {INTERESTS.map((i) => (
                      <button key={i.id} type="button" onClick={() => toggle(i.id)} aria-pressed={form.interests.includes(i.id)} data-testid={`join-interest-${i.id}`}
                        className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${form.interests.includes(i.id) ? "border-[#F59E0B] bg-[#F59E0B]/15 text-[#F59E0B]" : "border-white/15 text-gray-300 hover:border-white/40"}`}>
                        {i.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <p className="text-xs text-gray-500 leading-relaxed">
                  By joining, you opt in to email updates about Laugh at L.A. and the Laugh @ brand from Providence Film Group. Unsubscribe any time.
                </p>
                {status.type === "err" && <p role="alert" className="text-sm text-red-400" data-testid="join-error">{status.text}</p>}
                <button type="submit" disabled={loading} data-testid="join-submit"
                  className="w-full rounded-full bg-[#E11D48] px-8 py-4 font-bold text-white shadow-[0_0_25px_rgba(225,29,72,0.4)] transition-all hover:bg-[#F43F5E] disabled:opacity-60">
                  {loading ? "Saving your seat…" : "Join the List"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
